import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  InventoryGeneralFlatTeamSettingGroupById,
  type InventoryGeneralFlatTeamSettingGroupId,
} from "./groups";

type InventoryGeneralSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: InventoryGeneralSettingInput,
): InventoryGeneralSettingInput {
  return metadata;
}

function inventoryGeneralGroup<
  const Group extends InventoryGeneralFlatTeamSettingGroupId,
  const T extends Record<string, InventoryGeneralSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const InventoryGeneralRegistry = {
  ...inventoryGeneralGroup("inventory_general.cycle_count_variance", {
    "ims/cycleCountVariancePercent": setting({
      label: "Cycle count percent variance",
      description:
        "Percent tolerance a cycle count may differ from expected on-hand before it needs review. Null disables the percent check.",
      synonyms: [
        "cycle count percent variance",
        "percent variance",
        "count variance percent",
        "cycle count percent tolerance",
      ],
    }),
    "ims/cycleCountVarianceAmount": setting({
      label: "Cycle count dollar variance",
      description:
        "Dollar-value tolerance (in cents) a cycle count may differ from expected on-hand before it needs review. Null disables the dollar check.",
      synonyms: [
        "cycle count dollar variance",
        "dollar variance",
        "amount variance",
        "cycle count dollar tolerance",
      ],
    }),
  }),
};

type InventoryGeneralFlatTeamSettingKey = keyof typeof InventoryGeneralRegistry;
const InventoryGeneralFlatTeamSettingKeys = Object.keys(
  InventoryGeneralRegistry,
) as InventoryGeneralFlatTeamSettingKey[];

export const InventoryGeneralFlatTeamSettingCatalogDefinitions =
  InventoryGeneralFlatTeamSettingKeys.map((key) => {
    const definition = InventoryGeneralRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: InventoryGeneralFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    InventoryGeneralFlatTeamSettingKey,
    InventoryGeneralFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<InventoryGeneralFlatTeamSettingKey>
  >[];

export const InventoryGeneralFlatTeamSettingMetadataByKey = Object.fromEntries(
  InventoryGeneralFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<InventoryGeneralFlatTeamSettingKey, SettingMetadata>;
